import { isStandardLoyaltyLocale, Locale, ReviewsSortOption } from '@headless-workspace/config';
import { UserSephoraCard, UserSephoraCardUnlimited } from '@headless-workspace/domain/common/server';
import { PredicateHelpers } from '@headless-workspace/utils';
import { RatingsAndReviewsDTO, reviewDTO } from './RatingsAndReviewsDTO';

export enum ReviewPurchaserType {
    VERIFIED = 'verified',
    TESTER = 'tester',
    UNCONTROLLED = 'uncontrolled',
    SPONTANEOUS = 'spontaneous',
}

export type ReviewSortOption = {
    value: string;
    label: string;
    tag: ReviewsSortOption;
};

export type RatingFilterData = {
    ratingValue: number;
    reviewsCountPerRating: number;
    progressValue: number;
};

export type RatingsAndReviewsValue = {
    reviewCount: number;
    ratingFilter: RatingFilterData[];
    sortingOption: ReviewSortOption[];
    reviews: ProductReview[];
};

export type ProductReview = {
    id?: string;
    rating: number;
    createdAt: Date;
    userName: string;
    userGender?: string;
    userAge?: string;
    userCard?: UserSephoraCard | UserSephoraCardUnlimited;
    purchaserType: ReviewPurchaserType;
    purchaseProductSince?: string;
    title: string;
    content?: string;
    language: string;
    isRecommended?: boolean;
    source?: {
        label: string;
        href: string;
    };
    vote: {
        like: number;
        dislike: number;
    };
};

const findUserInfo = (data: reviewDTO['contextualData'], key: string): string | undefined => {
    const getObj = data?.find((item) => item.id === key);
    return getObj?.label;
};

const mapLoyalty = (
    loyalty: reviewDTO['loyaltyStatus'],
    locale: Locale,
): UserSephoraCard | UserSephoraCardUnlimited | undefined => {
    const hasLoyalty = loyalty !== UserSephoraCard.PreBuy && loyalty !== 'NONE';

    if (!hasLoyalty) {
        return undefined;
    }

    return isStandardLoyaltyLocale(locale) ? (loyalty as UserSephoraCard) : (loyalty as UserSephoraCardUnlimited);
};

const mapReview = (ProductReview: reviewDTO, locale: Locale): ProductReview => ({
    id: ProductReview.id,
    rating: ProductReview.rating,
    createdAt: new Date(ProductReview.createdAt),
    userName: ProductReview.userName ?? '',
    userGender: findUserInfo(ProductReview.contextualData, 'Gender'),
    userAge: findUserInfo(ProductReview.contextualData, 'Age'),
    userCard: mapLoyalty(ProductReview.loyaltyStatus, locale),
    purchaserType:
        ReviewPurchaserType[ProductReview.badge as keyof typeof ReviewPurchaserType] ??
        ReviewPurchaserType.UNCONTROLLED,
    purchaseProductSince: findUserInfo(ProductReview.contextualData, 'ExperienceDachat'),
    title: ProductReview.title ?? '',
    content: ProductReview.body ?? '',
    language: ProductReview.locale ?? '',
    isRecommended: ProductReview.isRecommended,
    source: ProductReview.syndication
        ? {
              label: ProductReview.syndication?.name ?? '',
              href: ProductReview.syndication?.url ?? '',
          }
        : undefined,
    vote: {
        like: ProductReview.helpfulness.positiveCount,
        dislike: ProductReview.helpfulness.negativeCount,
    },
});

export const mapRatingFilter = (
    ratingFilter: RatingsAndReviewsDTO['distribution'],
    count: number,
): RatingFilterData[] => {
    return [5, 4, 3, 2, 1].map((rating) => {
        const found = ratingFilter.find((item) => item.rating === rating);
        const reviewsCountPerRating = found?.count ?? 0;

        return {
            ratingValue: rating,
            reviewsCountPerRating,
            progressValue: count > 0 ? (reviewsCountPerRating / count) * 100 : 0,
        };
    });
};

export const mapSortingOption = (sorting: { id: string; label: string }[]): ReviewSortOption[] => {
    const priorityOrder: ReviewsSortOption[] = [
        ReviewsSortOption.MostRecent,
        ReviewsSortOption.LeastRecent,
        ReviewsSortOption.BestRated,
        ReviewsSortOption.WorstRated,
    ];
    const validOptionIds = new Set(Object.values(ReviewsSortOption));
    const validOptions = sorting.filter(({ id }) => validOptionIds.has(id as ReviewsSortOption));
    const result: ReviewSortOption[] = [];
    for (const id of priorityOrder) {
        const option = validOptions.find((option) => option.id === id);
        if (option) {
            result.push({
                value: option.id as ReviewsSortOption,
                tag: option.id as ReviewsSortOption,
                label: option.label,
            });
        }
    }
    return result;
};

export const mapRatingsAndReviews = (dto: RatingsAndReviewsDTO, locale: Locale): RatingsAndReviewsValue => {
    return {
        reviewCount: dto.count,
        ratingFilter: mapRatingFilter(dto.distribution, dto.count),
        sortingOption: mapSortingOption(dto.sorting),
        reviews: dto.reviews.map((review) => mapReview(review, locale)),
    };
};
